import { z } from 'zod/v4';

import { firstDefined, normalizeFlatArgs } from '../widget-schemas/fold-helpers';

/**
 * AI-facing schemas for the two preset tools — `get_theme_presets` and `apply_theme_preset`.
 *
 * **Why presets get their own pair of tools at all.** The three `update_theme_*` tools let a caller
 * set any value it can name, which is the right shape for "our brand colour is #1D4ED8". It is the
 * wrong shape for "make the site look more premium": there the model has to invent five harmonious
 * colours, or eleven type sizes, and it has no way to check the result. The shipped presets are the
 * designers' answers to exactly that question, and applying one is a single call instead of eleven.
 *
 * **Two tools, not one.** The catalogue is 20 palettes, 24 type scales and 9 button styles — far too
 * much to carry in `get_page_snapshot`, which is read on every planning step. So it is fetched only
 * when a preset is actually being chosen, and the apply takes the id that listing reported. Same
 * shape as every other id on this surface: it comes from a read, never from imagination.
 */

/** Which part of the theme a preset covers. A preset never spans two. */
const THEME_PRESET_PARTS = ['colors', 'fonts', 'buttons'] as const;

export type ThemePresetPart = (typeof THEME_PRESET_PARTS)[number];

const PART_DESCRIPTION =
    '"colors" is a five-colour palette, "fonts" is a whole type scale (family and size for every text ' +
    'style), "buttons" is both button styles at once.';

/** `palette`/`typography`/`buttonStyles` are the words a model reaches for instead of the three above. */
const PART_ALIASES: Readonly<Record<string, ThemePresetPart>> = {
    colors: 'colors',
    color: 'colors',
    colours: 'colors',
    palette: 'colors',
    fonts: 'fonts',
    font: 'fonts',
    typography: 'fonts',
    type: 'fonts',
    buttons: 'buttons',
    button: 'buttons',
    buttonstyles: 'buttons',
};

function foldPart(args: Record<string, unknown>): void {
    if (args['part'] === undefined) {
        const value = firstDefined(args['type'], args['kind'], args['section'], args['setting']);

        if (value !== undefined) {
            args['part'] = value;
        }
    }

    for (const key of ['kind', 'section', 'setting'] as const) {
        delete args[key];
    }

    // `type` is deleted only after it has had its chance above: it is also the word a model uses for
    // the part itself, and leaving it behind would make the object fail nothing but mean nothing.
    delete args['type'];

    const part = args['part'];

    if (typeof part === 'string') {
        const resolved = PART_ALIASES[part.trim().toLowerCase()];

        // An unrecognized word is left alone so the enum names it, rather than this fold silently
        // dropping the only argument the call had.
        if (resolved) {
            args['part'] = resolved;
        }
    }
}

const getThemePresetsObjectSchema = z.object({
    part: z.enum(THEME_PRESET_PARTS).describe(`Which catalogue to list. ${PART_DESCRIPTION}`),
});

export const getThemePresetsArgsSchema = z.preprocess(
    (raw) => normalizeFlatArgs(raw, foldPart),
    getThemePresetsObjectSchema
);

const applyThemePresetObjectSchema = z.object({
    part: z.enum(THEME_PRESET_PARTS).describe(`Which part the preset covers. ${PART_DESCRIPTION}`),
    presetId: z
        .string()
        .describe(
            "The preset's id, exactly as get_theme_presets reported it (a number in a string, e.g. " +
                '"-538663083"). Never invent one — call get_theme_presets for this part first.'
        ),
});

export type ApplyThemePresetArgs = z.infer<typeof applyThemePresetObjectSchema>;

export const applyThemePresetArgsSchema = z.preprocess(
    (raw) =>
        normalizeFlatArgs(raw, (args) => {
            foldPart(args);

            if (args['presetId'] === undefined) {
                const value = firstDefined(args['id'], args['preset'], args['index'], args['presetIndex']);

                if (value !== undefined) {
                    // Numbers arrive as numbers when the model copies the id without its quotes;
                    // the ids ARE numeric strings, so this is a spelling difference, not a type error.
                    args['presetId'] = typeof value === 'number' ? String(value) : value;
                }
            }

            for (const key of ['id', 'preset', 'index', 'presetIndex'] as const) {
                delete args[key];
            }
        }),
    applyThemePresetObjectSchema
);
